import React, { useEffect, createContext, useContext, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// import supabase from './utils/supabaseClient'; // Replace direct Supabase client with proxy
import proxyClient from './utils/proxyClient'; // Import the proxy client instead
import checkEnvironmentVariables from './utils/envCheck'; // Import the environment variable check
import Layout from './components/Layout';
import Login from './pages/Login';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';
import BorrowerForm, { BorrowerData, RentalProperty, Income } from './pages/BorrowerForm';
import UWNotesPage from './pages/uw-notes';
import BusinessNotesPage from './pages/BusinessNotesPage';
import AppraisalCriticality from './pages/AppraisalCriticality';
import AppraisalCRPage from './pages/AppraisalCRPage';
import ExceptionWriting from './pages/ExceptionWriting';
import EasyAccess from './pages/EasyAccess';
import Downloads from './pages/Downloads';
import OcrReader from './pages/OcrReader';
import { Box, CircularProgress, Typography } from '@mui/material';
import ProtectedRoute from './components/ProtectedRoute';
import { submitBorrowerForm } from './services/borrowerFormService';

interface FormState {
  dealNumber: string;
  dealType: string;
  closingDate: string;
  cof: string;
  bfsStage: string;
  propertyWatchlistStatus: string;
  employerWatchlistStatus: string;
  matchFilesStatus: string;
  matchFilesNotes?: string;
  propertyTaxAdjustment: string;
  dealBorrowerRelationshipStatus: string;
  dealBorrowerRelationshipOther?: string;
  propertyValueStatus: string;
  propertyValueOtherNotes?: string;
  mortgageAmount: string;
  mtgType: string;
  ltv: string;
  etoChecked: boolean;
  etoNotes: string;
  term: string;
  rate: string;
  amortization: string;
  ltvRestrictions: string;
  propertyInfo: string;
  downpaymentSource: string;
  downpaymentSourceNotes: string;
  downpaymentAmount: string;
  giftor: string;
  inheritingFrom: string;
  savingsStatus: string;
  sinceDateStatus: string;
  sinceDateOtherNotes?: string;
  borrowers: {
    [key: string]: BorrowerData;
  };
  rentalProperties: {
    [key: string]: RentalProperty;
  };
  incomes: {
    [key: string]: Income;
  };
  conditions: { [key: string]: boolean };
  elapsedTime?: number;
  startTime?: string;
  endTime?: string;
  submitAttempted?: boolean;
}

interface BorrowerSubmissionData extends BorrowerData {
  pr_rt: string;
  rt_description: string;
}

interface DealSubmissionPayload {
  dealNumber: string;
  dealType: string;
  closingDate: string;
  cof: string;
  bfsStage: string;
  propertyWatchlistStatus: string;
  employerWatchlistStatus: string;
  matchFilesStatus: string;
  matchFilesNotes?: string;
  propertyTaxAdjustment: string;
  dealBorrowerRelationshipStatus: string;
  dealBorrowerRelationshipOther?: string;
  propertyValueStatus: string;
  propertyValueOtherNotes?: string;
  mortgageAmount: string;
  mtgType: string;
  ltv: string;
  etoChecked: boolean;
  etoNotes: string;
  term: string;
  rate: string;
  amortization: string;
  ltvRestrictions: string;
  propertyInfo: string;
  province: string;
  downpaymentSource: string;
  downpaymentSourceNotes: string;
  downpaymentAmount: string;
  giftor: string;
  inheritingFrom: string;
  savingsStatus: string;
  sinceDateStatus: string;
  sinceDateOtherNotes?: string;
  borrowers: {
    [key: string]: BorrowerSubmissionData;
  };
  rentalProperties: {
    [key: string]: RentalProperty;
  };
  incomes: {
    [key: string]: Income;
  };
  conditions: { [key: string]: boolean };
  elapsedTime?: number;
  startTime?: string;
  endTime?: string;
}

// Create auth context
export const AuthContext = createContext<{
  user: any;
  loading: boolean;
}>({
  user: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" color="error">Something went wrong</Typography>
          <Typography color="error">{this.state.error?.message}</Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('App: Initializing authentication state');
    
    const checkSession = async () => {
      // Check URL for OAuth tokens from social login redirect
      const params = new URLSearchParams(window.location.search);
      const access_token = params.get('access_token');
      const refresh_token = params.get('refresh_token');
      const error = params.get('error');
      
      // Handle OAuth redirect errors
      if (error) {
        console.error('OAuth error:', error);
        const error_description = params.get('error_description');
        alert(`Login failed: ${error_description || error}`);
        window.history.replaceState(null, '', window.location.pathname); // Remove error from URL
        setUser(null);
        setLoading(false);
        return;
      }
      
      // If OAuth tokens are present in URL, set session
      if (access_token && refresh_token) {
        console.log('App: OAuth tokens found in URL, setting session');
        try {
          const { data, error } = await proxyClient.auth.setSession({
            access_token,
            refresh_token
          });
          
          if (error) {
            console.error('Error setting session from OAuth tokens:', error);
            setUser(null);
          } else if (data?.user) {
            console.log('App: Session set successfully from OAuth tokens');
            setUser(data.user);
          }
          
          // Clean URL by removing tokens
          window.history.replaceState(null, '', window.location.pathname);
        } catch (error) {
          console.error('Error processing OAuth tokens:', error);
        }
        
        setLoading(false);
        return;
      }
      
      // Normal session check if no OAuth tokens
      const { data: sessionData, error: sessionError } = await proxyClient.auth.getSession();
      console.log('App: Session check result:', sessionData?.session ? 'has session' : 'no session');
      
      if (sessionError) {
        console.error('Session check error:', sessionError);
        setUser(null);
        setLoading(false);
        return;
      }
      
      if (sessionData?.session?.user) {
        console.log('App: User found in session');
        setUser(sessionData.session.user);
      } else {
        console.log('App: No user in session');
        setUser(null);
      }
      
      setLoading(false);
    };
    
    // Setup auth state change listener
    const subscription = proxyClient.auth.onAuthStateChange(
      async (event, session) => {
        console.log('App: Auth state changed:', event, 'Session:', session ? 'exists' : 'none');
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            console.log('App: User signed in or token refreshed');
            setUser(session.user);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('App: User signed out');
          setUser(null);
        }
      }
    );
    
    checkSession();
    
    return () => {
      console.log('App: Cleaning up auth listener');
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log('App component mounted');
    // Check environment variables
    checkEnvironmentVariables();
  }, []);

  const handleBorrowerSubmit = async (formData: DealSubmissionPayload): Promise<boolean> => {
    console.log('[DEBUG] handleBorrowerSubmit called with formData:', JSON.stringify(formData, null, 2));
    try {
      if (!user) {
        console.error('No user logged in');
        return false;
      }

      // Use the submitBorrowerForm service function which calls the SQL function
      const response = await submitBorrowerForm(formData);
      
      if (response.success) {
        console.log('[DEBUG] Form submission successful with formId:', response.formId);
        return true;
      } else {
        console.error('[DEBUG] Form submission failed:', response.error);
        return false;
      }

    } catch (error) {
      console.error('Error in handleBorrowerSubmit:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, loading }}>
        <React.Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={
              <Layout>
                <ProtectedRoute requireAdmin>
                  <Admin />
                </ProtectedRoute>
              </Layout>
            } />
            <Route path="/borrower-form" element={
              <Layout>
                <ProtectedRoute>
                  <BorrowerForm onSubmit={handleBorrowerSubmit} />
                </ProtectedRoute>
              </Layout>
            } />
            <Route path="/dashboard" element={
              <Layout>
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              </Layout>
            } />
            <Route path="/uw-notes" element={
              <Layout>
                <ProtectedRoute>
                  <UWNotesPage />
                </ProtectedRoute>
              </Layout>
            } />
            
            {/* Business Notes Route */}
            <Route path="/business-notes" element={
              <Layout>
                <ProtectedRoute>
                  <BusinessNotesPage />
                </ProtectedRoute>
              </Layout>
            } />

            {/* Appraisal Criticality Route */}
            <Route path="/appraisal-criticality" element={
              <Layout>
                <ProtectedRoute>
                  <AppraisalCriticality />
                </ProtectedRoute>
              </Layout>
            } />

            {/* Appraisal CR Page Route */}
            <Route path="/appraisal-cr" element={
              <Layout>
                <ProtectedRoute>
                  <AppraisalCRPage />
                </ProtectedRoute>
              </Layout>
            } />

            {/* Exception Writing Route */}
            <Route path="/exception-writing" element={
              <Layout>
                <ProtectedRoute>
                  <ExceptionWriting />
                </ProtectedRoute>
              </Layout>
            } />

            {/* Easy Access / Useful Tools Route */}
            <Route path="/easy-access" element={
              <Layout>
                <ProtectedRoute>
                  <EasyAccess />
                </ProtectedRoute>
              </Layout>
            } />

            {/* Downloads Route */}
            <Route path="/downloads" element={
              <Layout>
                <ProtectedRoute>
                  <Downloads />
                </ProtectedRoute>
              </Layout>
            } />

            {/* OCR Reader Route */}
            <Route path="/ocr-reader" element={
              <Layout>
                <ProtectedRoute>
                  <OcrReader />
                </ProtectedRoute>
              </Layout>
            } />

            {/* --- ADDED PLACEHOLDER ROUTES --- */}
            {/* Replace path and element with your actual routes/components */}
            <Route path="/placeholder1" element={
              <Layout>
                <ProtectedRoute>
                  <Box sx={{ p: 2 }}><Typography variant="h4">Placeholder Page 1</Typography></Box>
                </ProtectedRoute>
              </Layout>
            } />
            <Route path="/placeholder2" element={
              <Layout>
                <ProtectedRoute>
                <Box sx={{ p: 2 }}><Typography variant="h4">Placeholder Page 2</Typography></Box>
                </ProtectedRoute>
              </Layout>
            } />
            {/* --- END PLACEHOLDER ROUTES --- */}

            <Route path="/" element={
              user ? (
                <Layout>
                  <Navigate to="/dashboard" />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            } />
          </Routes>
        </React.Suspense>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;